import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, API_URL } from "../lib/utils";
import { printInvoice } from "../lib/invoicePrint";
import { printPreBill } from "../lib/posPrint";
import { promotionFor } from "../lib/promotions";
import { buildCategoryTree, productInCategory, categoryPath } from "../lib/categories";
import { useBarcodeScanner, normalizeBarcode, beep } from "../lib/barcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { POSTopBar } from "../components/pos/POSTopBar";
import { POSCashLockOverlay } from "../components/pos/POSCashLockOverlay";
import { AppyPayWaitDialog } from "../components/payments/AppyPayWaitDialog";
import { POSCategoryTree } from "../components/pos/POSCategoryTree";
import { POSProductGrid } from "../components/pos/POSProductGrid";
import { POSOrderPanel } from "../components/pos/POSOrderPanel";
import { POSTablesView } from "../components/pos/POSTablesView";
import { POSOrdersView } from "../components/pos/POSOrdersView";
import { POSInvoicesView } from "../components/pos/POSInvoicesView";
import { POSPayModal } from "../components/pos/POSPayModal";
import { POSInvoiceModal } from "../components/pos/POSInvoiceModal";
import { POSOfflineBar } from "../components/pos/POSOfflineBar";
import { POSOfflinePayModal } from "../components/pos/POSOfflinePayModal";
import { POSCashCloseModal } from "../components/pos/POSCashCloseModal";
import { listOfflineSales, addOfflineSale, updateOfflineSale, removeOfflineSale, clearSyncedOfflineSales, savePosCache, loadPosCache, syncOfflineSales, listOfflineTableOrders, addOfflineTableItems, removeOfflineTableItem, removeOfflineTableOrder, offlineTableOrderBody, syncOfflineTableOrders } from "../lib/offlineSales";
import { getOfflineCash, openCashOffline, closeCashOffline, discardOfflineCashClose, hasPendingCashOpen, hasPendingCashClose, localCashSession, syncOfflineCashOpen, syncOfflineCashClose } from "../lib/offlineCash";

const OPEN_STATUSES = ["open", "preparing", "ready", "served"];
const todayStr = () => new Date().toISOString().slice(0, 10);
const errDetail = (e, fallback) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};
const EMPTY_FORM = { customerName: "", customerNif: "", showNif: false, orderType: "dine_in", waiterId: "", tableId: "", cookingTime: "", notes: "" };
// Pedido de mesa guardado offline → objeto com a forma de um pedido do servidor (para as vistas Mesas/Pedidos)
const offlineAsOrder = (o) => ({ ...o, order_id: o.local_id, order_number: `OFF-${o.local_id.slice(-4).toUpperCase()}`, order_type: "dine_in", status: "open", payment_status: "pending", requires_kitchen: true, offline: true });

const POSPage = () => {
  const { token, user } = useAuth();
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const api = useCallback(
    (method, url, data) =>
      axios({ method, url: `${API_URL}${url}`, data, headers, withCredentials: true }).catch((e) => {
        if (e.response?.status === 423) setCashSession(null);
        throw e;
      }),
    [headers]
  );

  const [view, setView] = useState("items");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [ads, setAds] = useState([]);
  const [promos, setPromos] = useState([]);
  const [adIndex, setAdIndex] = useState(0);
  const [waiters, setWaiters] = useState([]);
  const [settings, setSettings] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cashSession, setCashSession] = useState(undefined);

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [busy, setBusy] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payResult, setPayResult] = useState(null);
  const [appyCharge, setAppyCharge] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const activeOrderRef = useRef(null);
  useEffect(() => { activeOrderRef.current = activeOrder; }, [activeOrder]);

  // ---------- modo offline ----------
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [tpas, setTpas] = useState([]);
  const [offlineSales, setOfflineSales] = useState(() => listOfflineSales(user?.company_id));
  const [offlineTables, setOfflineTables] = useState(() => listOfflineTableOrders(user?.company_id));
  const [offlineCash, setOfflineCash] = useState(() => getOfflineCash(user?.company_id));
  const [offlinePayOpen, setOfflinePayOpen] = useState(false);
  const [offlineTarget, setOfflineTarget] = useState(null); // pedido (mesa) a fechar offline; null = carrinho
  const [closeCashOpen, setCloseCashOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);
  const refreshOffline = () => { setOfflineSales(listOfflineSales(user?.company_id)); setOfflineTables(listOfflineTableOrders(user?.company_id)); setOfflineCash(getOfflineCash(user?.company_id)); };
  const offlineMeta = () => ({ company_id: user?.company_id || null, user_id: user?.user_id || null, waiter_id: form.waiterId || null, waiter_name: waiterList.find((w) => w.user_id === form.waiterId)?.name || null, customer_name: form.customerName || null, notes: form.notes || null });

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  // Sincroniza o pedido ativo com o servidor (itens adicionados noutro terminal / QR da mesa, pagamentos, cancelamentos)
  const syncActiveOrder = (orders) => {
    const prev = activeOrderRef.current;
    if (!prev) return;
    const fresh = orders.find((o) => o.order_id === prev.order_id);
    if (!fresh) return;
    const gone = fresh.status === "cancelled" ? "cancelado" : fresh.status === "merged" ? `juntado ao pedido #${fresh.merged_into_order_number || ""}`.trim() : fresh.payment_status !== "pending" ? "faturado" : null;
    if (gone) {
      toast.info(`Pedido #${prev.order_number} foi ${gone} noutro terminal`, { id: `sync-${prev.order_id}`, position: "bottom-center" });
      setActiveOrder(null);
      return;
    }
    const sig = (o) => `${o.items.length}|${o.total}|${o.items.map((i) => `${i.item_id}:${i.quantity}`).join(",")}`;
    if (sig(fresh) !== sig(prev)) {
      toast.info(`Pedido #${prev.order_number} atualizado noutro terminal: ${fresh.items.length} itens · ${formatCurrency(fresh.total)}`, { id: `sync-${prev.order_id}`, position: "bottom-center" });
      setActiveOrder(fresh);
    }
  };

  // POS é sempre em tema escuro (como no design de referência)
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    root.classList.add("dark");
    return () => {
      if (!hadDark) root.classList.remove("dark");
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [cats, prods, tbls, ordrs, adsRes, promosRes, cashRes, tpaRes] = await Promise.all([
        api("get", "/categories"),
        api("get", "/products"),
        api("get", "/tables"),
        api("get", "/orders").catch(() => ({ data: [] })),
        api("get", "/ads/active").catch(() => ({ data: [] })),
        api("get", "/promotions/active").catch(() => ({ data: [] })),
        api("get", "/cash/current").catch(() => ({ data: undefined })),
        api("get", "/tpas").catch(() => ({ data: [] })),
      ]);
      setCategories(cats.data);
      setProducts(prods.data);
      setTables(tbls.data);
      setTpas(tpaRes.data || []);
      setOpenOrders((ordrs.data || []).filter((o) => OPEN_STATUSES.includes(o.status) && o.payment_status === "pending"));
      syncActiveOrder(ordrs.data || []);
      setAds(adsRes.data || []);
      setPromos(promosRes.data || []);
      if (cashRes.data !== undefined) setCashSession(cashRes.data || null);
      setOnline(true);
      savePosCache({ ...(loadPosCache() || {}), categories: cats.data, products: prods.data, tables: tbls.data, tpas: tpaRes.data || [], promos: promosRes.data || [], ...(cashRes.data !== undefined ? { cash: cashRes.data || null } : {}) });
    } catch (e) {
      if (!e.response) {
        // Sem ligação: usar o catálogo guardado neste dispositivo
        setOnline(false);
        const cache = loadPosCache();
        if (cache) {
          setCategories((c) => (c.length ? c : cache.categories || []));
          setProducts((p) => (p.length ? p : cache.products || []));
          setTables((t) => (t.length ? t : cache.tables || []));
          setTpas((t) => (t.length ? t : cache.tpas || []));
          setPromos((p) => (p.length ? p : cache.promos || []));
          if ("cash" in cache) setCashSession((c) => (c === undefined ? cache.cash : c));
        }
      } else {
        toast.error("Erro ao carregar dados do POS");
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const r = await api("get", `/invoices?date=${todayStr()}`);
      setInvoices(r.data);
    } catch (e) {
      toast.error("Erro ao carregar faturas");
    } finally {
      setInvoicesLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 8000);
    return () => clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    api("get", "/settings").then((r) => { setSettings(r.data); savePosCache({ ...(loadPosCache() || {}), settings: r.data }); }).catch(() => { const c = loadPosCache(); if (c?.settings) setSettings(c.settings); });
    api("get", "/pos/waiters").then((r) => setWaiters(r.data)).catch(() => setWaiters([]));
    fetchInvoices();
  }, [api, fetchInvoices]);

  // Sincronizar caixa, vendas e mesas offline assim que a ligação volta: abertura → vendas (pagam o pedido tal como estava) → pedidos de mesa abertos → fecho de caixa
  const runSync = useCallback(async (silent = false) => {
    if (syncingRef.current) return;
    const cashRec = getOfflineCash(user?.company_id);
    const hasSales = listOfflineSales(user?.company_id).some((s) => s.status !== "synced");
    const hasTables = listOfflineTableOrders(user?.company_id).length > 0;
    if (!hasSales && !hasTables && !hasPendingCashOpen(cashRec) && !hasPendingCashClose(cashRec)) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      let stopped = false;
      if (hasPendingCashOpen(cashRec)) {
        const c = await syncOfflineCashOpen(api, user?.company_id);
        if (c.offline) stopped = true;
        else if (c.ok) { setCashSession(c.session); toast.success(c.alreadyOpen ? "Abertura de caixa offline: já havia caixa aberta no servidor — as vendas offline entram nessa caixa" : `Caixa aberta no servidor (abertura offline de ${formatCurrency(c.session.opening_balance)} sincronizada)`, { duration: 7000 }); }
        else toast.error(`Abertura de caixa offline recusada: ${c.error}`, { duration: 8000 });
        refreshOffline();
      }
      const r = hasSales && !stopped ? await syncOfflineSales(api, user?.company_id) : { synced: 0, failed: 0, stoppedOffline: false, invoices: [] };
      const t = hasTables && !stopped && !r.stoppedOffline ? await syncOfflineTableOrders(api, user?.company_id) : { synced: 0, failed: 0, stoppedOffline: false, orders: [] };
      refreshOffline();
      if (r.synced) toast.success(`${r.synced} venda(s) offline faturada(s)${r.invoices.length ? `: ${r.invoices.map((i) => i.invoice_number).join(", ")}` : ""}`, { duration: 8000 });
      if (r.failed) toast.error(`${r.failed} venda(s) offline com erro — veja "Ver vendas" na barra laranja`, { duration: 8000 });
      if (t.synced) toast.success(`${t.synced} pedido(s) de mesa offline enviado(s) para o servidor${t.orders.some((o) => o.requires_kitchen !== false) ? " e cozinha" : ""}: ${t.orders.map((o) => `#${o.order_number}`).join(", ")}`, { duration: 8000 });
      if (t.failed) toast.error(`${t.failed} pedido(s) de mesa offline com erro — veja "Ver vendas" na barra laranja`, { duration: 8000 });
      const act = activeOrderRef.current;
      if (act?.offline) {
        const synced = t.orders.find((o) => o.client_ref === act.local_id);
        setActiveOrder(synced || (listOfflineTableOrders(user?.company_id).some((o) => o.local_id === act.local_id) ? act : null));
      }
      stopped = stopped || r.stoppedOffline || t.stoppedOffline;
      let closed = false;
      if (!stopped && hasPendingCashClose(getOfflineCash(user?.company_id))) {
        const left = listOfflineSales(user?.company_id).filter((s) => s.status !== "synced").length + listOfflineTableOrders(user?.company_id).length;
        if (left > 0) {
          toast.warning(`Fecho de caixa offline em espera: ${left} venda(s)/pedido(s) ainda por sincronizar (repita ou descarte na barra laranja)`, { duration: 8000 });
        } else {
          const c = await syncOfflineCashClose(api, user?.company_id);
          if (c.offline) stopped = true;
          else if (c.ok) {
            closed = true;
            const d = c.result.difference || 0;
            const tpaTxt = (c.result.tpa_closings || []).filter((x) => x.tpa_id).map((x) => `${x.name} ${x.difference > 0 ? "+" : ""}${formatCurrency(x.difference)}`).join(" · ");
            toast.success(`Caixa conciliada e fechada: contado ${formatCurrency(c.result.closing_balance)} · esperado ${formatCurrency(c.result.expected_cash)} · diferença ${d > 0 ? "+" : ""}${formatCurrency(d)}${tpaTxt ? ` · TPA: ${tpaTxt}` : ""}`, { duration: 12000 });
            setCashSession(null);
          } else toast.error(`Fecho de caixa offline recusado: ${c.error} — pode anular o fecho offline e fechar online`, { duration: 10000 });
          refreshOffline();
        }
      }
      if (stopped) setOnline(false);
      else if (!silent || r.synced || t.synced || closed) { fetchData(); fetchInvoices(); }
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [api, fetchData, fetchInvoices, user?.company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (online) runSync(true);
  }, [online, runSync]);

  useEffect(() => {
    if (view === "invoices") fetchInvoices();
  }, [view, fetchInvoices]);

  useEffect(() => {
    if (user?.user_id) setForm((f) => (f.waiterId ? f : { ...f, waiterId: user.user_id }));
  }, [user]);

  useEffect(() => {
    if (ads.length < 2) return;
    const id = setInterval(() => setAdIndex((i) => (i + 1) % ads.length), 6000);
    return () => clearInterval(id);
  }, [ads]);

  const waiterList = useMemo(() => {
    if (waiters.length || !user) return waiters;
    return [{ user_id: user.user_id, name: user.name }];
  }, [waiters, user]);

  const catTree = useMemo(() => buildCategoryTree(categories), [categories]);

  // ---------- carrinho ----------
  const addToCart = (product) => {
    if (product.track_stock) {
      const inCart = cart.find((i) => i.product_id === product.product_id)?.quantity || 0;
      const left = (product.current_stock ?? 0) - (inCart + 1);
      if (left < 0) {
        toast.error(`Sem stock suficiente de ${product.name} (disponível: ${product.current_stock ?? 0})`);
        return;
      }
      if (left <= (product.min_stock ?? 0)) {
        toast.warning(`Stock baixo: ${product.name} — ficam ${left} ${product.stock_unit || "un."} após esta venda (mínimo ${product.min_stock ?? 0})`, { id: `low-${product.product_id}` });
      }
    }
    setCart((prev) => {
      const ex = prev.find((i) => i.product_id === product.product_id);
      if (ex) return prev.map((i) => (i.product_id === product.product_id ? { ...i, quantity: i.quantity + 1 } : i));
      const { price, promo, original } = promotionFor(product, promos, catTree.parentOf);
      return [...prev, { ...product, price, original_price: promo ? original : null, promotion_name: promo?.name || null, quantity: 1, notes: "" }];
    });
  };
  const updateQty = (id, d) => setCart((prev) => prev.map((i) => (i.product_id === id ? { ...i, quantity: Math.max(0, i.quantity + d) } : i)).filter((i) => i.quantity > 0));
  const removeItem = (id) => setCart((prev) => prev.filter((i) => i.product_id !== id));
  const setNote = (id, notes) => setCart((prev) => prev.map((i) => (i.product_id === id ? { ...i, notes } : i)));
  const resetAll = () => {
    setCart([]);
    setActiveOrder(null);
    setForm((f) => ({ ...EMPTY_FORM, waiterId: f.waiterId }));
  };

  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const cartTax = cart.reduce((s, i) => {
    const r = i.tax_rate ?? 14;
    const line = i.price * i.quantity;
    return s + (r > 0 ? line - line / (1 + r / 100) : 0);
  }, 0);
  const grand = cartTotal + (activeOrder?.total || 0);
  const tax = cartTax + (activeOrder?.tax || 0);
  const totals = { subtotal: grand - tax, tax, grand };

  const catKind = useMemo(() => Object.fromEntries(categories.map((c) => [c.category_id, c.kind || "food"])), [categories]);
  const drinkItems = cart.filter((i) => catKind[i.category_id] === "drink");
  const foodItems = cart.filter((i) => catKind[i.category_id] !== "drink");
  const routing = {
    foodQty: foodItems.reduce((s, i) => s + i.quantity, 0),
    drinkQty: drinkItems.reduce((s, i) => s + i.quantity, 0),
    onlyDrinks: cart.length > 0 && foodItems.length === 0,
    mixed: foodItems.length > 0 && drinkItems.length > 0,
  };

  const filteredProducts = products.filter(
    (p) => p.is_available && productInCategory(p, selectedCategory, catTree.childrenOf) && (p.name.toLowerCase().includes(search.toLowerCase()) || (p.barcode && search && p.barcode.includes(normalizeBarcode(search))))
  );

  // Leitor de código de barras (teclado): código lido → artigo entra na conta
  const [lastScan, setLastScan] = useState(null);
  const addByBarcode = (raw) => {
    const code = normalizeBarcode(raw);
    if (!code) return false;
    const product = products.find((p) => p.barcode && p.barcode === code);
    if (!product) {
      beep(false);
      setLastScan({ ok: false, code });
      toast.error(`Código ${code} não corresponde a nenhum artigo`);
      return false;
    }
    if (!product.is_available) {
      beep(false);
      toast.error(`${product.name} está indisponível`);
      return false;
    }
    addToCart(product);
    beep(true);
    setLastScan({ ok: true, name: product.name, code });
    setSearch("");
    return true;
  };
  useBarcodeScanner(addByBarcode, { enabled: view === "items" });
  const onSearchEnter = (value) => {
    const code = normalizeBarcode(value);
    if (products.some((p) => p.barcode === code)) { addByBarcode(code); return; }
    if (filteredProducts.length === 1) { addToCart(filteredProducts[0]); setSearch(""); }
  };
  const categoryName = selectedCategory === "all" ? "Todos os Artigos" : categoryPath(categories, selectedCategory, "Artigos");
  // Pedidos abertos = servidor (menos os já fechados offline à espera de sincronizar) + pedidos de mesa guardados offline
  const allOpenOrders = useMemo(() => {
    const paidOffline = new Set(offlineSales.filter((s) => s.status !== "synced" && s.server_order_id).map((s) => s.server_order_id));
    return [...openOrders.filter((o) => !paidOffline.has(o.order_id)), ...offlineTables.map(offlineAsOrder)];
  }, [openOrders, offlineTables, offlineSales]);
  const unpaidTableIds = useMemo(() => new Set(allOpenOrders.map((o) => o.table_id).filter(Boolean)), [allOpenOrders]);
  // Caixa "vista" pelo POS: fecho offline pendente → bloqueada; abertura offline pendente → sessão local; offline → sessão em cache + vendas offline
  const effectiveCash = useMemo(() => {
    if (hasPendingCashClose(offlineCash)) return null;
    if (hasPendingCashOpen(offlineCash)) return localCashSession(null, offlineCash, offlineSales, tpas);
    if (!online) return localCashSession(cashSession || null, null, offlineSales, tpas) ?? cashSession;
    return cashSession;
  }, [offlineCash, offlineSales, tpas, online, cashSession]);
  const pendingSyncCount = offlineSales.filter((s) => s.status !== "synced").length + offlineTables.length;
  const tableLabelOf = (order) => {
    const t = tables.find((x) => x.table_id === order?.table_id);
    if (t) return `Mesa ${t.number}`;
    return order?.order_type === "delivery" ? "Entrega" : "Balcão / Take-away";
  };

  // ---------- pedidos ----------
  const buildOrderData = () => ({
    table_id: form.orderType === "dine_in" ? form.tableId || null : null,
    order_type: form.orderType,
    customer_name: form.customerName || null,
    notes: form.notes || null,
    waiter_id: form.waiterId || null,
    waiter_name: waiterList.find((w) => w.user_id === form.waiterId)?.name || null,
    cooking_time: form.cookingTime || null,
    items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity, notes: i.notes || null })),
  });

  const validateNew = () => {
    if (cart.length === 0) return toast.error("Adicione artigos ao pedido"), false;
    if (form.orderType === "dine_in" && !form.tableId) return toast.error("Selecione a mesa (ou mude o tipo para Balcão / Take-away)"), false;
    return true;
  };

  const placeOrder = async () => {
    if (!validateNew()) return;
    if (!online) {
      if (form.orderType !== "dine_in" || !form.tableId) return toast.error("Sem ligação: pedidos de balcão/entrega só com \"Pagar Agora\" (venda offline). Pedidos para mesa ficam guardados neste dispositivo.");
      const t = tables.find((x) => x.table_id === form.tableId);
      const rec = addOfflineTableItems(form.tableId, cart, { ...offlineMeta(), table_label: t ? `Mesa ${t.number}` : "Mesa" });
      refreshOffline();
      toast.success(`${rec.table_label}: pedido guardado OFFLINE (${rec.items.length} itens · ${formatCurrency(rec.total)}) — vai para o servidor e cozinha quando a ligação voltar`, { duration: 6000 });
      resetAll();
      return;
    }
    const existing = form.orderType === "dine_in" ? openOrderForTable(form.tableId) : null;
    if (existing) {
      setActiveOrder(existing);
      return addToOrder(existing);
    }
    setBusy(true);
    try {
      const r = await api("post", "/orders", buildOrderData());
      const o = r.data;
      if (o.merged_into_existing) {
        toast.success(`Mesa já tinha o pedido #${o.order_number} aberto — artigos adicionados a esse pedido (total ${formatCurrency(o.total)})`);
      } else {
        const where = o.requires_kitchen === false ? "bebidas no balcão (não vai à cozinha)" : o.has_drinks ? "comida → cozinha · bebidas → balcão" : "enviado para a cozinha";
        toast.success(`Pedido #${o.order_number} criado — ${where}. ${o.table_id ? `${tableLabelOf(o)}: NÃO PAGA` : ""}`);
      }
      resetAll();
      fetchData();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao criar pedido"));
    } finally {
      setBusy(false);
    }
  };

  const addToOrder = async (orderArg) => {
    const target = orderArg?.order_id ? orderArg : activeOrder;
    if (!target || cart.length === 0) return toast.error("Adicione artigos");
    if (target.offline || !online) {
      // Offline: os artigos ficam no pedido local da mesa (juntam-se ao pedido do servidor ao sincronizar)
      if (!target.table_id) return toast.error("Sem ligação: não é possível adicionar artigos a um pedido de balcão. Use \"Pagar Agora\".");
      const rec = addOfflineTableItems(target.table_id, cart, { ...offlineMeta(), table_label: tableLabelOf(target) });
      refreshOffline();
      setActiveOrder(target.offline ? offlineAsOrder(rec) : target);
      setCart([]);
      toast.success(`Itens guardados OFFLINE na ${rec.table_label} — total offline ${formatCurrency(rec.total)}`);
      return;
    }
    setBusy(true);
    try {
      const items = cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity, notes: i.notes || null }));
      const r = await api("put", `/orders/${target.order_id}/items`, items);
      toast.success(`Itens adicionados ao pedido #${target.order_number} — total ${formatCurrency(r.data.total)}`);
      setActiveOrder(r.data);
      setCart([]);
      fetchData();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao adicionar itens"));
    } finally {
      setBusy(false);
    }
  };

  const openOrder = (order) => {
    setActiveOrder(order);
    setCart([]);
    setView("items");
  };

  const openOrderForTable = (tableId) => allOpenOrders.filter((o) => o.table_id === tableId).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))[0] || null;

  // Selecionar no formulário uma mesa que já tem pedido aberto → passa a trabalhar nesse pedido (mantém o carrinho)
  const onTableChosen = (tableId) => {
    setField("tableId", tableId);
    const existing = tableId ? openOrderForTable(tableId) : null;
    if (existing) {
      setActiveOrder(existing);
      toast.info(`Mesa com pedido #${existing.order_number} aberto — os artigos serão adicionados a esse pedido`, { position: "bottom-center" });
    }
  };

  const selectFreeTable = (t) => {
    const existing = openOrderForTable(t.table_id);
    if (existing) return openOrder(existing);
    setActiveOrder(null);
    setForm((f) => ({ ...f, orderType: "dine_in", tableId: t.table_id }));
    setView("items");
    toast.info(`Mesa ${t.number} selecionada — adicione os artigos`);
  };

  const mergeTableOrders = async (t) => {
    if (!window.confirm(`Juntar todos os pedidos abertos da Mesa ${t.number} num só pedido? Os artigos ficam todos no pedido mais antigo e os restantes são encerrados como "juntados".`)) return;
    try {
      const r = await api("post", `/tables/${t.table_id}/merge-orders`);
      toast.success(`Pedidos #${r.data.merged_order_numbers.join(", #")} juntados ao pedido #${r.data.order_number} — total ${formatCurrency(r.data.total)}`, { position: "bottom-center" });
      if (activeOrderRef.current?.table_id === t.table_id) setActiveOrder(r.data);
      fetchData();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao juntar pedidos"));
    }
  };

  const releaseTable = async (t) => {
    try {
      const r = await api("put", `/tables/${t.table_id}/status?status=free`);
      setTables((prev) => prev.map((x) => (x.table_id === t.table_id ? { ...x, ...r.data } : x)));
      toast.success(`Mesa ${t.number} libertada`);
      fetchData();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao libertar mesa"));
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    if (cancelTarget.offline) {
      removeOfflineTableOrder(cancelTarget.local_id);
      refreshOffline();
      if (activeOrder?.order_id === cancelTarget.order_id) resetAll();
      setCancelTarget(null);
      return toast.success(`Pedido offline ${cancelTarget.order_number} descartado (nunca chegou ao servidor)`);
    }
    if (!online) return toast.error("Sem ligação: só é possível cancelar pedidos do servidor quando a ligação voltar");
    try {
      const r = await api("put", `/orders/${cancelTarget.order_id}/status?status=cancelled`);
      const restored = r.data?.stock_restored_items || [];
      toast.success(
        restored.length
          ? `Pedido #${cancelTarget.order_number} cancelado — stock reposto: ${restored.join(", ")}`
          : `Pedido #${cancelTarget.order_number} cancelado`
      );
      if (activeOrder?.order_id === cancelTarget.order_id) resetAll();
      setCancelTarget(null);
      fetchData();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao cancelar pedido"));
    }
  };

  const openCash = async (balance, notes) => {
    if (!online) {
      openCashOffline({ company_id: user?.company_id || null, user_id: user?.user_id || null, user_name: user?.name || "", opening_balance: balance, notes });
      refreshOffline();
      toast.success(`Caixa aberta OFFLINE com fundo ${formatCurrency(balance)} — a abertura é enviada ao servidor quando a ligação voltar`, { duration: 6000 });
      return;
    }
    setBusy(true);
    try {
      const r = await api("post", "/cash/open", { opening_balance: balance, notes: notes || null });
      setCashSession(r.data);
      toast.success(`Caixa aberto com fundo ${formatCurrency(balance)} — pode começar a vender`);
    } catch (e) {
      if (e.response?.status === 400 && /Já existe/i.test(e.response?.data?.detail || "")) {
        const cur = await api("get", "/cash/current").catch(() => ({ data: null }));
        setCashSession(cur.data || null);
      } else {
        toast.error(errDetail(e, "Erro ao abrir caixa"));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmCloseCash = async (c) => {
    if (!effectiveCash) return;
    if (!online || effectiveCash.offline) {
      closeCashOffline(user?.company_id || null, c);
      refreshOffline();
      setCloseCashOpen(false);
      resetAll();
      toast.success(`Caixa fechada OFFLINE (contado ${formatCurrency(c.closing_balance)}) — será conciliada com o servidor quando a ligação voltar`, { duration: 7000 });
      return;
    }
    setBusy(true);
    try {
      const r = await api("post", "/cash/close", { closing_balance: c.closing_balance, notes: c.notes, tpa_counts: c.tpa_counts });
      const d = r.data.difference || 0;
      const tpaTxt = (r.data.tpa_closings || []).filter((x) => x.tpa_id).map((x) => `${x.name} ${x.difference > 0 ? "+" : ""}${formatCurrency(x.difference)}`).join(" · ");
      toast.success(`Caixa fechada: contado ${formatCurrency(r.data.closing_balance)} · esperado ${formatCurrency(r.data.expected_cash)} · diferença ${d > 0 ? "+" : ""}${formatCurrency(d)}${tpaTxt ? ` · TPA: ${tpaTxt}` : ""}`, { duration: 10000 });
      setCloseCashOpen(false);
      setCashSession(null);
      resetAll();
      fetchData();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao fechar caixa"));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemoveExisting = async () => {
    if (!removeTarget || !activeOrder) return;
    if (activeOrder.offline) {
      const rec = removeOfflineTableItem(activeOrder.local_id, removeTarget.item_id);
      refreshOffline();
      setActiveOrder(rec ? offlineAsOrder(rec) : null);
      setRemoveTarget(null);
      return toast.success(`${removeTarget.product_name} x${removeTarget.quantity} removido do pedido offline${rec ? "" : " — pedido vazio descartado"}`);
    }
    setBusy(true);
    try {
      const r = await api("delete", `/orders/${activeOrder.order_id}/items/${removeTarget.item_id}`);
      const { removed_item, stock_after, ...order } = r.data;
      toast.success(
        stock_after !== null && stock_after !== undefined
          ? `${removed_item.product_name} x${removed_item.quantity} removido — stock reposto (agora ${stock_after})`
          : `${removed_item.product_name} x${removed_item.quantity} removido do pedido #${order.order_number}`
      );
      setActiveOrder(order);
      setRemoveTarget(null);
      fetchData();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao remover item"));
    } finally {
      setBusy(false);
    }
  };

  const reprintOrderInvoice = async (orderId) => {
    if (!orderId) return toast.error("Sem fatura associada");
    try {
      const r = await api("get", `/orders/${orderId}/invoice`);
      printInvoice(r.data, settings || {});
    } catch (e) {
      toast.error("Fatura não encontrada");
    }
  };

  const preBill = async (order) => {
    const target = order || activeOrder;
    if (!target) return toast.error("Grave o pedido antes de emitir a consulta de mesa");
    if (!order && cart.length) return toast.error("Envie os artigos pendentes antes da consulta de mesa");
    if (!navigator.onLine) {
      return printPreBill(target, settings || {}, `${tableLabelOf(target)} · TALÃO PROVISÓRIO (offline) — a consulta de mesa é emitida com ligação`);
    }
    try {
      // Consulta de mesa (CM): documento de conferência assinado e numerado — Decreto 312/18, Anexo I n.º 4
      const r = await axios.post(`${API_URL}/orders/${target.order_id}/conference`, {}, { withCredentials: true });
      printInvoice({ ...r.data, table_name: r.data.table_name || tableLabelOf(target) }, settings || {});
      toast.success(`Consulta de mesa ${r.data.document_number} emitida`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erro ao emitir consulta de mesa");
    }
  };

  // ---------- pagamento / faturação ----------
  const payNow = () => {
    if (!validateNew()) return;
    if (!online) return setOfflinePayOpen(true);
    const existing = form.orderType === "dine_in" ? openOrderForTable(form.tableId) : null;
    if (existing) {
      setActiveOrder(existing);
      return toast.warning(`A mesa já tem o pedido #${existing.order_number} aberto — adicione os artigos a esse pedido e use "Fechar & Faturar"`);
    }
    setPayTarget({
      mode: "cart",
      title: "Pagar Agora & Emitir Fatura",
      subtitle: "Cria o pedido, regista o pagamento e emite a Fatura-Recibo numa só ação.",
      items: cart.map((i) => ({ name: i.name, quantity: i.quantity, total: i.price * i.quantity })),
      total: cartTotal,
    });
  };

  const closeTable = (order) => {
    const o = order || activeOrder;
    if (!o) return;
    if (o.order_id === activeOrder?.order_id && cart.length > 0) return toast.error("Adicione primeiro os itens pendentes ao pedido (ou limpe-os)");
    if (o.offline && online) { toast.info("A enviar o pedido offline para o servidor — volte a clicar em Faturar daqui a instantes"); runSync(); return; }
    if (o.offline || !online) { setOfflineTarget(o); setOfflinePayOpen(true); return; }
    setPayTarget({
      mode: "order",
      order: o,
      title: `Fechar ${tableLabelOf(o)} — Pedido #${o.order_number}`,
      subtitle: "Ao confirmar, a conta é fechada, a fatura emitida e a mesa fica PAGA (verde).",
      items: o.items.map((i) => ({ name: i.product_name, quantity: i.quantity, total: i.line_total ?? i.unit_price * i.quantity })),
      total: o.total,
    });
  };

  const confirmOfflinePay = (p) => {
    if (offlineTarget) {
      // Fechar uma mesa offline: pedido local (criado neste dispositivo) ou pedido do servidor em cache
      const o = offlineTarget;
      const isLocal = !!o.offline;
      const rec = addOfflineSale({
        company_id: user?.company_id || null,
        user_id: user?.user_id || null,
        order: isLocal ? offlineTableOrderBody(o) : { table_id: o.table_id || null, order_type: o.order_type || "dine_in", items: [] },
        server_order_id: isLocal ? null : o.order_id,
        server_order_number: isLocal ? null : o.order_number,
        items: o.items.map((i) => ({ product_id: i.product_id, name: i.product_name, quantity: i.quantity, price: i.unit_price, unit: i.unit })),
        total: o.total,
        tax: o.tax || 0,
        payment: p,
        table_label: tableLabelOf(o),
      });
      if (isLocal) removeOfflineTableOrder(o.local_id);
      refreshOffline();
      setOfflineTarget(null);
      setOfflinePayOpen(false);
      if (p.print) printPreBill({ items: o.items, total: o.total, tax: o.tax || 0 }, settings || {}, `${tableLabelOf(o)} · TALÃO PROVISÓRIO (fecho offline ${rec.local_id.slice(-5).toUpperCase()}) · ${p.label}${p.tpa_name ? ` ${p.tpa_name}` : ""} · a fatura é emitida ao sincronizar`);
      if (activeOrder?.order_id === o.order_id) resetAll();
      toast.success(`${tableLabelOf(o)} fechada OFFLINE (${formatCurrency(o.total)}) — a fatura é emitida automaticamente quando a ligação voltar`, { duration: 6000 });
      return;
    }
    const t = tables.find((x) => x.table_id === form.tableId);
    const rec = addOfflineSale({
      company_id: user?.company_id || null,
      user_id: user?.user_id || null,
      order: buildOrderData(),
      items: cart.map((i) => ({ product_id: i.product_id, name: i.name, quantity: i.quantity, price: i.price, unit: i.stock_unit })),
      total: cartTotal,
      tax: cartTax,
      payment: p,
      table_label: t ? `Mesa ${t.number}` : "Balcão / Take-away",
    });
    refreshOffline();
    setOfflinePayOpen(false);
    if (p.print) {
      printPreBill(
        { items: cart.map((i) => ({ product_name: i.name, quantity: i.quantity, unit: i.stock_unit, unit_price: i.price, line_total: i.price * i.quantity })), total: cartTotal, tax: cartTax },
        settings || {},
        `TALÃO PROVISÓRIO (venda offline ${rec.local_id.slice(-5).toUpperCase()}) · ${p.label}${p.tpa_name ? ` ${p.tpa_name}` : ""} · a fatura é emitida ao sincronizar`
      );
    }
    toast.success(`Venda offline guardada (${formatCurrency(cartTotal)}) — será faturada automaticamente quando a ligação voltar`, { duration: 6000 });
    resetAll();
  };

  const retryOfflineSale = async (s) => {
    updateOfflineSale(s.local_id, { status: "pending", error: null });
    refreshOffline();
    await runSync();
  };
  const discardOfflineSale = (s) => { removeOfflineSale(s.local_id); refreshOffline(); };

  const confirmPay = async (p) => {
    if (!payTarget) return;
    setBusy(true);
    try {
      let order = payTarget.order;
      if (payTarget.mode === "cart") {
        order = (await api("post", "/orders", buildOrderData())).data;
        if (order.merged_into_existing) {
          setPayTarget(null);
          setCart([]);
          setActiveOrder(order);
          fetchData();
          toast.warning(`A ${tableLabelOf(order)} já tinha o pedido #${order.order_number} aberto — os artigos foram juntados. Confirme o total (${formatCurrency(order.total)}) e use "Fechar & Faturar".`);
          return;
        }
      }
      let result;
      if (p.method === "appypay") {
        const r = await api("post", `/orders/${order.order_id}/pay/appypay`, { phone_number: p.phone, customer_name: p.customerName || null, customer_nif: p.customerNif || null });
        setAppyCharge({ ...r.data, _order: order });
        setPayTarget(null);
        return;
      }
      if (p.method === "credit") {
        const r = await api("put", `/orders/${order.order_id}/credit?customer_id=${encodeURIComponent(p.creditCustomerId)}`);
        result = { invoice: r.data.invoice, credit: true, customer_balance: r.data.customer_balance };
      } else {
        const params = new URLSearchParams({ payment_method: p.method });
        if (p.customerName) params.append("customer_name", p.customerName);
        if (p.customerNif) params.append("customer_nif", p.customerNif);
        if (p.method === "cash" && p.amountReceived !== null) params.append("amount_received", p.amountReceived);
        if (p.method === "giftcard") params.append("gift_card_code", p.giftCardCode);
        if (p.method === "card" && p.tpaId) params.append("tpa_id", p.tpaId);
        result = (await api("put", `/orders/${order.order_id}/pay?${params.toString()}`, p.method === "mixed" ? p.mixed : undefined)).data;
      }
      setPayResult({ ...result, tableLabel: tableLabelOf(order), orderNumber: order.order_number });
      setPayTarget(null);
      resetAll();
      fetchData();
      fetchInvoices();
    } catch (e) {
      toast.error(errDetail(e, "Erro ao fechar a conta"));
    } finally {
      setBusy(false);
    }
  };

  const onAppyPaid = (charge) => {
    const order = appyCharge?._order;
    const inv = charge.result?.invoice;
    setAppyCharge(null);
    if (inv) setPayResult({ invoice: inv, order: charge.result?.order, tableLabel: order ? tableLabelOf(order) : "", orderNumber: order?.order_number, appypay: true });
    toast.success(`Pagamento Multicaixa Express confirmado${charge.simulated ? " (SIMULADO)" : ""}`);
    resetAll();
    fetchData();
    fetchInvoices();
  };

  const goHome = () => {
    window.location.href = "/dashboard";
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#161616]">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-[#161616] text-zinc-100 overflow-hidden select-none relative" data-testid="pos-page">
      {effectiveCash === null && (
        <POSCashLockOverlay
          onOpen={openCash}
          busy={busy}
          offline={!online}
          online={online}
          syncing={syncing}
          pendingClose={hasPendingCashClose(offlineCash) ? offlineCash.close : null}
          pendingSales={pendingSyncCount}
          onSync={() => runSync()}
          onDiscardClose={() => { discardOfflineCashClose(); refreshOffline(); toast.info("Fecho offline anulado — a caixa continua aberta neste terminal"); }}
        />
      )}
      <POSTopBar
        view={view}
        setView={setView}
        counts={{ orders: allOpenOrders.length, tables: unpaidTableIds.size, invoices: invoices.length }}
        user={user}
        cashSession={effectiveCash}
        onHome={goHome}
        onKitchen={() => { window.location.href = "/kitchen"; }}
        onCloseCash={() => setCloseCashOpen(true)}
      />
      <POSOfflineBar online={online} sales={offlineSales} tableOrders={offlineTables} cash={offlineCash} syncing={syncing} onSync={() => runSync()} onRetry={retryOfflineSale} onDiscard={discardOfflineSale} onClearSynced={() => { clearSyncedOfflineSales(); refreshOffline(); }} />
      <div className="flex-1 flex min-h-0">
        <POSCategoryTree categories={categories} products={products} selectedCategory={selectedCategory} onSelectCategory={(id) => { setSelectedCategory(id); setView("items"); }} onAddProduct={addToCart} />
        <main className="flex-1 min-w-0 min-h-0 flex flex-col bg-[#1e1e1e]">
          {view === "items" && (
            <POSProductGrid products={filteredProducts} onAdd={addToCart} search={search} setSearch={setSearch} onSearchEnter={onSearchEnter} lastScan={lastScan} categoryName={categoryName} ad={ads[adIndex]} adCount={ads.length} adIndex={adIndex} promos={promos} parentOf={catTree.parentOf} />
          )}
          {view === "orders" && <POSOrdersView openOrders={allOpenOrders} tables={tables} onOpenOrder={openOrder} onCloseTable={closeTable} onPreBill={preBill} onCancel={setCancelTarget} />}
          {view === "tables" && (
            <POSTablesView tables={tables} openOrders={allOpenOrders} onOpenOrder={openOrder} onSelectFree={selectFreeTable} onRelease={releaseTable} onReprint={reprintOrderInvoice} onCloseTable={closeTable} onMergeOrders={mergeTableOrders} />
          )}
          {view === "invoices" && <POSInvoicesView invoices={invoices} loading={invoicesLoading} onPrint={(inv) => printInvoice(inv, settings || {})} onRefresh={fetchInvoices} />}
        </main>
        <POSOrderPanel
          activeOrder={activeOrder}
          tableLabel={activeOrder ? tableLabelOf(activeOrder) : ""}
          form={form}
          setField={setField}
          tables={tables}
          waiters={waiterList}
          unpaidTableIds={unpaidTableIds}
          cart={cart}
          onQty={updateQty}
          onRemove={removeItem}
          onNote={setNote}
          onRemoveExisting={setRemoveTarget}
          totals={totals}
          routing={routing}
          busy={busy}
          onClear={() => setCart([])}
          onPreBill={() => preBill(null)}
          onPayNow={payNow}
          onPlaceOrder={placeOrder}
          onAddToOrder={() => addToOrder()}
          onCloseTable={() => closeTable(null)}
          onCancelOrder={() => setCancelTarget(activeOrder)}
          onExitOrder={resetAll}
          onTableChosen={onTableChosen}
        />
      </div>

      <POSPayModal open={!!payTarget} onClose={() => setPayTarget(null)} target={payTarget} onConfirm={confirmPay} processing={busy} token={token} initialCustomer={{ name: payTarget?.order?.customer_name || form.customerName, nif: form.customerNif }} />
      <POSOfflinePayModal
        open={offlinePayOpen}
        onClose={() => { setOfflinePayOpen(false); setOfflineTarget(null); }}
        total={offlineTarget ? offlineTarget.total : cartTotal}
        items={offlineTarget ? offlineTarget.items.map((i) => ({ product_id: i.product_id, name: i.product_name, quantity: i.quantity, price: i.unit_price })) : cart}
        tpas={tpas}
        onConfirm={confirmOfflinePay}
        title={offlineTarget ? `Fechar ${tableLabelOf(offlineTarget)} offline` : undefined}
        initialCustomer={{ name: offlineTarget?.customer_name || form.customerName, nif: form.customerNif }}
      />
      <POSInvoiceModal result={payResult} onClose={() => setPayResult(null)} settings={settings} />
      <POSCashCloseModal open={closeCashOpen} onClose={() => setCloseCashOpen(false)} session={effectiveCash} online={online} onConfirm={confirmCloseCash} busy={busy} />
      <AppyPayWaitDialog charge={appyCharge} headers={headers} onSuccess={onAppyPaid} onClose={() => { setAppyCharge(null); fetchData(); }} />

      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="pos-remove-item-modal">
          <DialogHeader>
            <DialogTitle>Remover {removeTarget?.product_name} x{removeTarget?.quantity}?</DialogTitle>
            <DialogDescription>
              O item sai do pedido #{activeOrder?.order_number}, o total é recalculado e o stock do artigo é reposto imediatamente. A remoção fica registada na auditoria.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveTarget(null)}>Voltar</Button>
            <Button variant="destructive" onClick={confirmRemoveExisting} disabled={busy} data-testid="pos-confirm-remove-item-btn">Remover Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="pos-cancel-modal">
          <DialogHeader>
            <DialogTitle>Cancelar pedido #{cancelTarget?.order_number}?</DialogTitle>
            <DialogDescription>O pedido será marcado como cancelado, a mesa libertada e o stock dos artigos reposto automaticamente. Esta ação não pode ser desfeita.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelTarget(null)}>Voltar</Button>
            <Button variant="destructive" onClick={confirmCancel} data-testid="pos-confirm-cancel-btn">Cancelar Pedido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSPage;
